import { Component, Injectable } from "@angular/core";
import * as XLSX from "xlsx";
import { ExcelPreviewComponent } from "../excel-preview/excel-preview.component";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";

export interface excelDataAndDataTypeInterface {
  header?: string;
  name: string;
  dataType: string;
  required?: boolean;
}

@Component({
  selector: "app-bulk-upload",
  templateUrl: "./bulk-upload.component.html",
  styleUrls: ["./bulk-upload.component.css"],
})
@Injectable({
  providedIn: "root",
})
export class BulkUploadComponent {

  private readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
  private readonly MAX_SHEETS = 10;
  private readonly MAX_ROWS_PER_SHEET = 5000;

  constructor(private modalService: NgbModal) {}

  async fileDataValidationAndObjectList(
    requireEntriesList: excelDataAndDataTypeInterface[],
    file: File,
    previewWindowOpen: boolean = true
  ): Promise<any> {
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!file) {
  return "No file selected.";
}

if (file.size === 0) {
  return "The uploaded file is empty.";
}

if (file.size > this.MAX_FILE_SIZE) {
  return "The uploaded file is too large. Maximum allowed size is 5 MB.";
}
    let tableHeaders: string[] = [];
    let tableData: any[] = [];

    if (["xlsx", "xls", "xlsm", "xlsb", "ods", "csv", "tsv"].includes(ext!)) {
      let rawExcelCamelHeaders: string[] = [];

      await new Promise<void>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const data = new Uint8Array(reader.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: "array" });
            if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
  reject("The workbook does not contain any sheets.");
  return;
}

if (workbook.SheetNames.length > this.MAX_SHEETS) {
  reject(
    `The workbook contains too many sheets. Maximum allowed is ${this.MAX_SHEETS}.`
  );
  return;
}
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: false,
              dateNF: "yyyy-mm-dd hh:mm:ss",
            });

          if (jsonData.length > this.MAX_ROWS_PER_SHEET + 1) {
            reject(`The workbook contains too many rows. Maximum allowed is ${this.MAX_ROWS_PER_SHEET}.`);
            return;
          }

            if (jsonData.length > 0) {
              const excelHeaders = (jsonData[0] as any[]).map(
                (h) => h?.toString().trim() ?? ""
              );
              const camelHeaders = excelHeaders.map((h) => this.toCamelCase(h));

              rawExcelCamelHeaders = [...camelHeaders];

              const headerToNameMap: Record<string, string> = {};
              excelHeaders.forEach((_, i) => {
                const camel = camelHeaders[i];
                const match = requireEntriesList.find((req) => {
                  const matchKey = req.header ?? req.name;
                  return this.toCamelCase(matchKey).toLowerCase() === camel.toLowerCase();
                });
                headerToNameMap[camel] = match ? match.name : camel;
              });

              tableData = jsonData
                .slice(1)
                .filter((row: any[]) =>
                  row.some((cell) => cell != null && cell.toString().trim() !== "")
                )
                .map((row: any[]) => {
                  const obj: any = {};
                  camelHeaders.forEach((camelHeader, i) => {
                    const canonicalKey = headerToNameMap[camelHeader] ?? camelHeader;
                    const cellValue = row[i];
                    const requiredField = requireEntriesList.find(
                      (r) => r.name.toLowerCase() === canonicalKey.toLowerCase()
                    );
                    const dataType = requiredField?.dataType?.toLowerCase();

                    if (typeof cellValue === "number") {
                      const parsed = XLSX.SSF.parse_date_code(cellValue);
                      if (parsed) {
                        obj[canonicalKey] = this.formatDateByType(parsed, dataType);
                      } else {
                        obj[canonicalKey] = cellValue;
                      }
                    } else if (this.isDateLike(cellValue)) {
                      obj[canonicalKey] = this.normalizeDateString(cellValue.toString(), dataType);
                    } else {
                      obj[canonicalKey] = cellValue ?? "";
                    }
                  });
                  return obj;
                });

              tableHeaders = camelHeaders.map((camel) => {
                const canonical = headerToNameMap[camel] ?? camel;
                const entry = requireEntriesList.find(
                  (r) => r.name.toLowerCase() === canonical.toLowerCase()
                );
                return entry ? entry.name : camel;
              });
            }
            resolve();
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });

      const missingRequired = requireEntriesList
        .filter((req) => {
          const expectedCamel = this.toCamelCase(req.header ?? req.name).toLowerCase();
          return !rawExcelCamelHeaders.some(
            (th) => th.toLowerCase() === expectedCamel
          );
        })
        .map((r) => r.header ?? r.name);

      if (missingRequired.length > 0) {
        return `Validation failed. Missing required entries: ${missingRequired.join(", ")}`;
      }

      if (previewWindowOpen === false) {
        return { tableHeaders, tableData };
      }

      const displayHeaders = tableHeaders.map((canonicalName) => {
        const entry = requireEntriesList.find(
          (r) => r.name.toLowerCase() === canonicalName.toLowerCase()
        );
        return entry?.header ?? this.toNormalHeader(canonicalName);
      });

      console.log("display header", displayHeaders);

      return this.triggerExcelPreviewAndReturnValidExcelData(
        tableHeaders,
        displayHeaders,
        tableData,
        file,
        requireEntriesList
      );
    } else {
      return "The uploaded file is not an Excel file.";
    }
  }

  private toCamelCase(header: string): string {
    return header
      .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
      .replace(/^./, (str) => str.toLowerCase())
      .trim();
  }

  private toNormalHeader(camel: string): string {
    return camel
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  private formatDateByType(
    parsed: { y: number; m: number; d: number; H: number; M: number; S: number },
    type?: string
  ): string {
    const yyyy = parsed.y;
    const mm = String(parsed.m).padStart(2, "0");
    const dd = String(parsed.d).padStart(2, "0");
    const hh = String(parsed.H).padStart(2, "0");
    const mi = String(parsed.M).padStart(2, "0");
    const ss = String(parsed.S).padStart(2, "0");

    switch (type) {
      case "date":  return `${yyyy}-${mm}-${dd}`;
      case "time":  return `${hh}:${mi}:${ss}`;
      default:      return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
    }
  }

  private isDateLike(value: any): boolean {
    if (typeof value !== "string") return false;
    return /[/-]/.test(value) && /\d{1,4}/.test(value);
  }

  private normalizeDateString(value: string, type?: string): string {
    const parsedDate = new Date(value);
    if (isNaN(parsedDate.getTime())) return value;

    const yyyy = parsedDate.getFullYear();
    const mm = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const dd = String(parsedDate.getDate()).padStart(2, "0");
    const hh = String(parsedDate.getHours()).padStart(2, "0");
    const mi = String(parsedDate.getMinutes()).padStart(2, "0");
    const ss = String(parsedDate.getSeconds()).padStart(2, "0");

    switch (type) {
      case "date":  return `${yyyy}-${mm}-${dd}`;
      case "time":  return `${hh}:${mi}:${ss}`;
      default:      return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
    }
  }

  private triggerExcelPreviewAndReturnValidExcelData(
    camelHeaders: string[],
    displayHeaders: string[],
    tableData: any[],
    file: File,
    requireEntriesList: excelDataAndDataTypeInterface[]
  ): Promise<any> {
    const modalRef: NgbModalRef = this.modalService.open(ExcelPreviewComponent, {
      size: "xl",
      centered: true,
      scrollable: false,
      backdrop: "static",
      windowClass: "custom-modal-zindex",
    });

    modalRef.componentInstance.tableHeaders = displayHeaders;
    modalRef.componentInstance.canonicalHeaders = camelHeaders;
    modalRef.componentInstance.tableData = tableData;
    modalRef.componentInstance.file = file;
    modalRef.componentInstance.requireEntriesList = requireEntriesList;

    return new Promise((resolve) => {
      modalRef.result
        .then(() => {
          resolve({ tableHeaders: camelHeaders, tableData });
        })
        .catch(() => {
          resolve("Excel Upload canceled by user");
        });
    });
  }
}